import React, { FC, ReactNode, useEffect, useState } from 'react'
import { ChevronDownIcon } from 'lucide-react'

import { TeamEmpty } from '../../empty/TeamEmpty/TeamEmpty'
import { Button } from '../../../../../ui/button'
import { TeamTabItem } from './components/TeamTabItem/TeamTabItem'

import RiderTeamMemberService from '../../../../../../services/firebase-service/rider-team-member/RiderTeamMemberService'
import UserService from '../../../../../../services/firebase-service/users/userService'

import {
  getUserRequiredFields,
  hasInvalidRequiredField,
} from '../../../../../../helpers/requiredFields'
import { cn } from '../../../../../../lib/utils'

import { IRiderTeamMemberInterface } from '../../../../../../models/rider-team-member/riderTeamMember.interface'
import { IUserInterface } from '../../../../../../models/users/user.interface'
import { IEventReviewPublish } from '../../../../../../models/event-review-publish/event-review-publish.interface'

import { CONST } from '../../../../../../const/const'

interface TeamTabProps {
  isOpenedDefault?: boolean
  type?: 'overview' | 'registrar'
  className?: string
  isCollapsible?: boolean
  registrationId: string
  user: IUserInterface | null
  publishedEvent: IEventReviewPublish | null
  onActionClick?: () => void
  actionIcon?: ReactNode
  isLoading?: boolean
}

export const TeamTab: FC<TeamTabProps> = ({
  isOpenedDefault,
  type,
  className,
  isCollapsible,
  registrationId,
  user,
  publishedEvent,
  onActionClick,
  actionIcon,
  isLoading,
}) => {
  const [loading, setLoading] = useState(true)
  const [riderTeamMembersExtended, setRiderTeamMembersExtended] = useState<
    { teamMember: IRiderTeamMemberInterface; user: IUserInterface }[]
  >([])
  const [filteredTeamMembers, setFilteredTeamMembers] = useState<
    { teamMember: IRiderTeamMemberInterface; user: IUserInterface }[]
  >([])
  const [totalIssues, setTotalIssues] = useState(0)
  const [isOpen, setIsOpen] = useState(!isCollapsible || isOpenedDefault)
  const [users, setUsers] = useState<IUserInterface[]>([])
  const [riderTeamMembers, setRiderTeamMembers] = useState<IRiderTeamMemberInterface[]>([])

  useEffect(() => {
    let filtered
    const eventRequiredUserFields = publishedEvent?.EventRequiredFields.requiredFields || []

    if (type === 'overview') {
      filtered = riderTeamMembersExtended.filter((riderTeamMember) => {
        const fields = getUserRequiredFields({
          user: riderTeamMember.user,
          eventRequiredUserFields,
        })

        const fieldsFiltered = fields.filter((field) => !hasInvalidRequiredField(field))

        return fields.length - fieldsFiltered.length > 0
      })
    } else {
      filtered = riderTeamMembersExtended
    }
    setFilteredTeamMembers(filtered)
  }, [publishedEvent?.EventRequiredFields.requiredFields, riderTeamMembersExtended, type])

  useEffect(() => {
    const getData = async () => {
      if (!user?.id) return

      try {
        setLoading(true)
        const riderTeamMembers_ = await RiderTeamMemberService.getByRider(registrationId, user.id)
        const uniqueRiderTeamMembersInDb: IRiderTeamMemberInterface[] = []
        const teamMembersIds: string[] = []

        riderTeamMembers_.forEach((riderTeamMember) => {
          const isExist = uniqueRiderTeamMembersInDb.find(
            (current) =>
              current.teamMemberId === riderTeamMember.teamMemberId &&
              current.teamMemberRole === riderTeamMember.teamMemberRole
          )
          if (
            !isExist?.id &&
            riderTeamMember.teamMemberRole !== CONST.UI.TEAM_MEMBERS_ROLES.RIDER
          ) {
            uniqueRiderTeamMembersInDb.push(riderTeamMember)
            if (riderTeamMember.teamMemberId) teamMembersIds.push(riderTeamMember.teamMemberId)
          }
        })

        const usersInDb = await UserService.getByIds(teamMembersIds)

        setUsers(usersInDb)
        setRiderTeamMembers(uniqueRiderTeamMembersInDb)
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }
    if (!isLoading) getData().then()
  }, [isLoading, registrationId, user?.id])

  useEffect(() => {
    const uniqueRiderTeamMembersExtended = riderTeamMembers.map((riderTeamMember) => {
      const user = users.find((user) => user.id === riderTeamMember.teamMemberId) as IUserInterface
      return { teamMember: riderTeamMember, user }
    })

    setRiderTeamMembersExtended(uniqueRiderTeamMembersExtended)
  }, [riderTeamMembers, users])

  useEffect(() => {
    let totalCount = 0

    filteredTeamMembers.forEach((riderTeamMember) => {
      const eventRequiredUserFields = publishedEvent?.EventRequiredFields.requiredFields || []
      const fields = getUserRequiredFields({
        user: riderTeamMember.user,
        eventRequiredUserFields,
      })

      const fieldsFiltered = fields.filter((field) => !hasInvalidRequiredField(field))
      totalCount += fields.length - fieldsFiltered.length || 0
    })

    setTotalIssues(totalCount)
  }, [filteredTeamMembers, publishedEvent])

  if (loading || isLoading) return <TeamEmpty />

  return (
    <div
      className={cn(
        'rounded-lg border border-black/10 px-7 pt-3',
        !isOpen && 'pb-3',
        !filteredTeamMembers?.[0] && 'py-3',
        className
      )}
    >
      <div className="flex gap-2 flex-wrap items-center justify-between">
        <div>
          <h4 className="font-medium">Team members</h4>
          <p className="text-xs text-black/50 mt-1">
            {type === 'overview'
              ? `, ${totalIssues > 0 ? `${totalIssues} issues` : 'No issues'}`
              : `${filteredTeamMembers.length} team members`}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {type === 'overview' ? (
            <Button
              variant="link"
              type="button"
              className="rounded-full p-0"
              onClick={onActionClick}
            >
              {actionIcon}
            </Button>
          ) : (
            totalIssues > 0 && (
              <div className="py-0.5 px-4 text-base font-normal rounded-full border border-transparent bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor">
                {totalIssues} issue{totalIssues > 1 ? 's' : ''}
              </div>
            )
          )}
          {isCollapsible && (
            <Button
              variant="link"
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                'rounded-full transition-all duration-200 w-[30px] h-[30px] flex items-center justify-center shrink-0',
                isOpen
                  ? 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rotate-180'
                  : 'text-black bg-black/5'
              )}
            >
              <ChevronDownIcon className="size-4" />
            </Button>
          )}
        </div>
      </div>
      {isOpen && (
        <div className={cn((!isCollapsible || isOpen) && type !== 'overview' && 'mt-3')}>
          {filteredTeamMembers.map((riderTeamMember, index) => (
            <TeamTabItem
              key={index}
              type={type}
              index={index}
              teamMember={riderTeamMember.teamMember}
              user={riderTeamMember.user}
              setUser={(value) => {
                const usersToUpdate = users.map((user) => (user.id === value.id ? value : user))
                setUsers(usersToUpdate)
              }}
              eventRequiredUserFields={publishedEvent?.EventRequiredFields.requiredFields}
            />
          ))}
        </div>
      )}
    </div>
  )
}
